import React, {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { DynamicConfiguration, ManagerApiClient } from '@loyalty/shared-common/manager-api-client';
import { QlfPlaceholder } from '@qualifio/ui-commons';
import { configureApiClient } from '@qualifio/api-client/dist/browser';
import { Loader } from './shared/layout/Loader';
import { useLoaders } from './shared/context/context-utils';
import { useSelectiveEffect } from './shared/use-selective-effect';

export interface Configuration extends DynamicConfiguration {
  elasticApmServiceVersion: string;
}

export const ConfigurationContext = createContext<Configuration>({} as any);

export function useConfiguration(): Configuration {
  return useContext(ConfigurationContext);
}

const staticConfiguration = {
  elasticApmServiceVersion: BUILD_VERSION,
} as Configuration;

const managerApi = configureApiClient(ManagerApiClient, { pathPrefix: BASE_PATH });

export function ConfigurationProvider({ children }: { children: ReactNode }): ReactElement {
  const [context, setContext] = useState<Configuration>({} as any);
  const { addLoader, removeLoader, isLoading } = useLoaders();
  const [error, setError] = useState<Error>();

  const getConfiguration = useCallback(() => {
    addLoader('getConfiguration');

    managerApi
      .getConfiguration()
      .then(dynamicConfiguration =>
        setContext({
          ...staticConfiguration,
          ...dynamicConfiguration,
        }),
      )
      .catch(error => {
        setError(error);
        console.error('Could not load configuration', error);
      })
      .finally(() => removeLoader('getConfiguration'));
  }, [addLoader, removeLoader]);

  useSelectiveEffect(
    () => {
      getConfiguration();
    },
    [getConfiguration],
    [],
  );

  if (error) {
    return <QlfPlaceholder primaryText="Error" secondaryText="Could not load configuration" />;
  }

  return isLoading('getConfiguration') ? (
    <Loader />
  ) : (
    <ConfigurationContext.Provider value={context}>{children}</ConfigurationContext.Provider>
  );
}
